import { useRoute, useRouter } from "vue-router";
import { computed, WritableComputedRef } from "vue";

type QueryValue = string | number | boolean | undefined;
type QueryValueGetter<T> = (value: string | undefined) => T;

export function useQueryManager() {
  const route = useRoute();
  const router = useRouter();

  const updateQuery = async (updates: Record<string, string | undefined>) => {
    const newQuery = {
      ...route.query,
      ...updates,
    };

    await router.push({
      name: route.name as string,
      query: newQuery,
    });
  };

  const getQueryValue = <T extends QueryValue>(
    key: string,
    parser: QueryValueGetter<T>
  ): WritableComputedRef<T> => {
    return computed({
      get: () => parser(route.query[key]?.toString()),
      set: (value: T) =>
        updateQuery({
          [key]: value === undefined ? undefined : value.toString(),
        }),
    });
  };

  /**
   * Like `getQueryValue`, but a missing or invalid param reads as `defaultValue`, and writing
   * `defaultValue` removes the param so the default stays the unqualified URL.
   */
  const getQueryValueWithDefault = <T extends string | number | boolean>(
    key: string,
    parser: QueryValueGetter<T | undefined>,
    defaultValue: T
  ): WritableComputedRef<T> =>
    computed({
      get: () => parser(route.query[key]?.toString()) ?? defaultValue,
      set: (value: T) =>
        updateQuery({
          [key]: value === defaultValue ? undefined : value.toString(),
        }),
    });

  const parsers = {
    boolean: (value?: string) => value === "true",
    number: (value?: string) => (value ? parseInt(value, 10) : undefined),
    /** A record id: digits only, positive, and exact as a JS number. Anything else reads as absent. */
    positiveId: (value?: string) =>
      value &&
      /^\d+$/.test(value) &&
      Number.isSafeInteger(Number(value)) &&
      Number(value) > 0
        ? Number(value)
        : undefined,
    /** Any date `Date.parse` accepts, normalized to an ISO instant. */
    isoInstant: (value?: string) =>
      value && Number.isFinite(Date.parse(value))
        ? new Date(value).toISOString()
        : undefined,
    string: (value?: string) => value,
    stringWithDefault:
      (defaultValue: string) =>
      (value?: string): string =>
        value || defaultValue,
    enum:
      <T extends string>(values: T[]) =>
      (value?: string): T | undefined =>
        value && values.includes(value as T) ? (value as T) : undefined,
    userType: (value?: string) => {
      if (value === "private" || value === "company") {
        return value;
      }
      return undefined;
    },
  };

  return {
    updateQuery,
    getQueryValue,
    getQueryValueWithDefault,
    parsers,
  };
}
